import type { Worksheet } from "exceljs";
import { DateTime } from "luxon";
import { formatPersonFullName } from "../utils/formatters";
import { type BreakRulesConfig, type HospitalData, type Nurse, type NurseInShift, NurseRole } from "../utils/models";
import { buildNurseNotes, getShiftCensus, getShiftWorkHours, inverseRolesMap } from "../utils/utilFunctions";
import { addBorders, addHorizontalDividers, addStaffingSummaryTableFloors, addTitle } from "./downloadDayView";

const NURSE_COLS_AMOUNT = 5;

const dayFloorStaff = ["OBS CNA", "SA", "A CNA", "B CNA", "C CNA", "", "A Nurse", "B Nurse", "C Nurse"];
const nightFloorStaff = ["OBS CNA", "A CNA", "B CNA", "C CNA", "", "A Nurse", "B Nurse", "C Nurse"];

function setColumnWidths(worksheet: Worksheet) {
	worksheet.columns = [
		{ width: 21 }, // Column A - 2rd floor assignments
		{ width: 23 }, // Column B - Names
		{ width: 5 }, // Column C - Role
		{ width: 9 }, // Column D - employment type
		{ width: 9 }, // Column E - Notes
		{ width: 5 }, // Column F - Empty column
		{ width: 21 }, // Column G - 3rd floor assignments
		{ width: 23 }, // Column H - Names
		{ width: 5 }, // Column I - Role
		{ width: 9 }, // Column J - employment type
		{ width: 9 }, // Column K - Notes
	];
}

function addDayAndDateFields(worksheet: Worksheet, day: string | undefined, date: string | undefined) {
	// Create separate cells for DAY and DATE
	const dayCell = worksheet.getCell("A3");
	const dateCell = worksheet.getCell("A4");

	// Set values
	dayCell.value = `DAY: ${day}`;
	dateCell.value = `DATE: ${date}`;

	// Add borders
	[dayCell, dateCell].forEach((cell) => {
		cell.border = {
			top: { style: "thin" },
			left: { style: "thin" },
			bottom: { style: "thin" },
			right: { style: "thin" },
		};
		cell.font = { bold: true };
	});
}

function calculateTotalNursingHours(shiftToNurseMap: any, departmentId: string, breakRulesConfig: BreakRulesConfig[], jobManagementEnabled = false) {
	let totalNursingHours = 0;
	Object.keys(shiftToNurseMap).forEach((floor) => {
		Object.keys(shiftToNurseMap[floor]).forEach((shift) => {
			if (shiftToNurseMap[floor][shift]) {
				Object.keys(shiftToNurseMap[floor][shift]).forEach((key) => {
					if (key !== "census") {
						shiftToNurseMap[floor][shift][key].forEach((nurse: any) => {
							const { hours, startTime } = nurse;

							if (typeof hours === "number" && DateTime.isDateTime(startTime)) {
								totalNursingHours += getShiftWorkHours(
									departmentId,
									startTime.plus({ hours: hours }),
									startTime,
									breakRulesConfig,
									nurse.role,
									nurse.type,
									nurse.jobId ? [nurse.jobId] : [],
									jobManagementEnabled,
									nurse.union ?? "",
								);
							}
						});
					}
				});
			}
		});
	});
	return totalNursingHours;
}

function addCensusAndStaffingData(
	worksheet: Worksheet,
	shiftToNurseMap: any,
	departmentId: string,
	breakRulesConfig: BreakRulesConfig[],
	jobManagementEnabled = false,
) {
	const first_col = String.fromCharCode("A".charCodeAt(0) + NURSE_COLS_AMOUNT + 1);
	const second_col = String.fromCharCode("A".charCodeAt(0) + NURSE_COLS_AMOUNT + 2);
	// Move census data to right side, aligned with day/date
	worksheet.getCell(`${first_col}3`).value = "Total Census";
	worksheet.getCell(`${first_col}4`).value = "Total Nursing Hours";
	worksheet.getCell(`${first_col}5`).value = "HPPD";

	// Calculate total census
	const secondFloorCensus = (shiftToNurseMap["2ND FLOOR"][0].census + shiftToNurseMap["2ND FLOOR"][1].census) / 2;
	// const thirdFloorCensus = (shiftToNurseMap['3RD FLOOR'][0]['census'] + shiftToNurseMap['3RD FLOOR'][1]['census']) / 2;
	const totalCensus = secondFloorCensus; // + thirdFloorCensus;

	// Calculate total nursing hours
	const totalNursingHours = calculateTotalNursingHours(shiftToNurseMap, departmentId, breakRulesConfig, jobManagementEnabled);
	// Set values
	worksheet.getCell(`${second_col}3`).value = totalCensus;
	worksheet.getCell(`${second_col}4`).value = Number.isInteger(totalNursingHours) ? totalNursingHours : Number(totalNursingHours.toFixed(2)); // Total Nursing Hours
	worksheet.getCell(`${second_col}5`).value = Number.isInteger(totalNursingHours / totalCensus)
		? totalNursingHours / totalCensus
		: Number((totalNursingHours / totalCensus).toFixed(2)); // PPD

	// Add borders to all census cells
	[first_col, second_col].forEach((col) => {
		[3, 4, 5].forEach((number) => {
			worksheet.getCell(`${col}${number}`).border = {
				top: { style: "thin" },
				left: { style: "thin" },
				bottom: { style: "thin" },
				right: { style: "thin" },
			};
		});
	});
}

function addFloorHeaders(worksheet: Worksheet) {
	const cols_2nd_floor = Array.from({ length: NURSE_COLS_AMOUNT }, (_, i) => String.fromCharCode("A".charCodeAt(0) + i));
	const cols_3rd_floor = Array.from({ length: NURSE_COLS_AMOUNT }, (_, i) => String.fromCharCode("A".charCodeAt(0) + i + NURSE_COLS_AMOUNT + 1));

	Object.entries({
		"2nd Floor": cols_2nd_floor,
		"3rd Floor": cols_3rd_floor,
	}).forEach(([name, cols]) => {
		worksheet.getCell(`${cols[0]}7`).value = name;
		worksheet.mergeCells(`${cols[0]}7:${cols[cols.length - 1]}7`);
		worksheet.getCell(`${cols[0]}7`).alignment = { horizontal: "center" };
		worksheet.getCell(`${cols[0]}7`).font = { size: 18, bold: true };
	});
}

function handleNurseEntry(
	worksheet: Worksheet,
	nurse: any,
	floorStaff: any,
	i: number,
	j: number,
	currentRow: number,
	cols: string[],
	floorShifts: any,
	isSA: boolean,
	floorName: string,
) {
	// Find how many consecutive empty rows follow this one
	let emptyRowCount = 0;
	for (let k = j + 1; k < floorShifts[floorStaff[i]].length; k++) {
		if (floorShifts[floorStaff[i]][k].nurse === "") {
			emptyRowCount++;
		} else {
			break;
		}
	}

	if (emptyRowCount > 0) {
		// If this row has data and is followed by empty rows, merge them
		const firstColValue = isSA ? `${floorName} SHOWER AIDE & \nDINING ROOM` : `${floorStaff[i]} ${nurse.nurse === "" ? "" : nurse.shift}`;

		worksheet.getCell(`${cols[0]}${currentRow}`).value = firstColValue;
		worksheet.getCell(`${cols[1]}${currentRow}`).value = nurse.nurse;
		worksheet.getCell(`${cols[2]}${currentRow}`).value = nurse.role;
		worksheet.getCell(`${cols[3]}${currentRow}`).value = nurse.employmentType;
		worksheet.getCell(`${cols[4]}${currentRow}`).value = nurse.notes;

		// Merge each column vertically
		worksheet.mergeCells(`${cols[0]}${currentRow}:${cols[0]}${currentRow + emptyRowCount}`);
		worksheet.mergeCells(`${cols[1]}${currentRow}:${cols[1]}${currentRow + emptyRowCount}`);
		worksheet.mergeCells(`${cols[2]}${currentRow}:${cols[2]}${currentRow + emptyRowCount}`);
		worksheet.mergeCells(`${cols[3]}${currentRow}:${cols[3]}${currentRow + emptyRowCount}`);
		worksheet.mergeCells(`${cols[4]}${currentRow}:${cols[4]}${currentRow + emptyRowCount}`);

		return {
			skipRows: emptyRowCount,
			newCurrentRow: currentRow + emptyRowCount,
		};
	}
	if (nurse.nurse !== "") {
		worksheet.getCell(`${cols[0]}${currentRow}`).value = isSA ? `${floorName} SHOWER AIDE & \nDINING ROOM  ${nurse.shift}` : `${floorStaff[i]} ${nurse.shift}`;
		worksheet.getCell(`${cols[1]}${currentRow}`).value = nurse.nurse;
		worksheet.getCell(`${cols[2]}${currentRow}`).value = nurse.role;
		worksheet.getCell(`${cols[3]}${currentRow}`).value = nurse.employmentType;
		worksheet.getCell(`${cols[4]}${currentRow}`).value = nurse.notes;
	}

	return { skipRows: 0, newCurrentRow: currentRow };
}

function insertFloorValues(worksheet: Worksheet, floorName: string, cols: string[], currentRow: number, floorStaff: any, floorShifts: any) {
	let cnaRowCount = 0;
	for (let i = 0; i < floorStaff.length; i++) {
		if (floorStaff[i] === "") {
			worksheet.getCell(`${cols[0]}${currentRow}`).value = floorStaff[i];
			currentRow++;
			continue;
		}
		if (floorStaff[i] === "SA") {
			worksheet.getCell(`${cols[0]}${currentRow}`).value = `${floorName} SHOWER AIDE & \nDINING ROOM`;
			if (floorShifts === undefined || !(floorStaff[i] in floorShifts)) {
				worksheet.getCell(`${cols[1]}${currentRow}`).value = "";
			} else {
				for (let j = 0; j < floorShifts[floorStaff[i]].length; j++) {
					cnaRowCount += 1;
					const nurse = floorShifts[floorStaff[i]][j];
					worksheet.getCell(`${cols[1]}${currentRow}`).value = nurse.nurse;
					worksheet.getCell(`${cols[2]}${currentRow}`).value = nurse.role;
					worksheet.getCell(`${cols[3]}${currentRow}`).value = nurse.employmentType;
					worksheet.getCell(`${cols[4]}${currentRow}`).value = nurse.notes;
					const result = handleNurseEntry(worksheet, nurse, floorStaff, i, j, currentRow, cols, floorShifts, floorStaff[i] === "SA", floorName);
					j += result.skipRows;
					currentRow = result.newCurrentRow;

					if (j < floorShifts[floorStaff[i]].length - 1) {
						currentRow++;
					}
				}
			}
		} else {
			if (floorShifts === undefined || !(floorStaff[i] in floorShifts)) {
				worksheet.getCell(`${cols[0]}${currentRow}`).value = `${floorStaff[i]}`;
				worksheet.getCell(`${cols[1]}${currentRow}`).value = "";
				if (floorStaff[i].includes("CNA")) {
					cnaRowCount += 1;
				}
			} else {
				for (let j = 0; j < floorShifts[floorStaff[i]].length; j++) {
					const nurse = floorShifts[floorStaff[i]][j];
					worksheet.getCell(`${cols[0]}${currentRow}`).value = `${floorStaff[i]} ${nurse.shift}`;
					worksheet.getCell(`${cols[1]}${currentRow}`).value = nurse.nurse;
					worksheet.getCell(`${cols[2]}${currentRow}`).value = nurse.role;
					worksheet.getCell(`${cols[3]}${currentRow}`).value = nurse.employmentType;
					worksheet.getCell(`${cols[4]}${currentRow}`).value = nurse.notes;

					const result = handleNurseEntry(worksheet, nurse, floorStaff, i, j, currentRow, cols, floorShifts, floorStaff[i] === "SA", floorName);
					j += result.skipRows;
					currentRow = result.newCurrentRow;

					if (j < floorShifts[floorStaff[i]].length - 1) {
						currentRow++;
					}
				}
				if (floorStaff[i].includes("CNA")) {
					cnaRowCount += floorShifts[floorStaff[i]].length;
				}
			}
		}
		worksheet.getCell(`${cols[0]}${currentRow}`).alignment = {
			wrapText: true,
		};
		worksheet.getCell(`${cols[0]}${currentRow}`).font = { size: 12 };
		worksheet.getCell(`${cols[1]}${currentRow}`).alignment = {
			wrapText: true,
		};
		worksheet.getCell(`${cols[1]}${currentRow}`).font = { size: 12 };

		currentRow++;
	}
	return cnaRowCount;
}

function addDayShiftStaff(worksheet: Worksheet, shiftToNurseMap: any, startRow: number) {
	const secondFloorShifts = shiftToNurseMap["2ND FLOOR"]?.[0];
	const secondFloorStaff = ["2OBS CNA", "SA", "2A CNA", "2B CNA", "2C CNA", "", "2A Nurse", "2B Nurse", "2C Nurse"];

	let cols = Array.from({ length: NURSE_COLS_AMOUNT }, (_, i) => String.fromCharCode("A".charCodeAt(0) + i));
	const cnaRowCountSecondFloor = insertFloorValues(worksheet, "2ND", cols, startRow, secondFloorStaff, secondFloorShifts);

	const thirdFloorShifts = shiftToNurseMap["3RD FLOOR"]?.[0];
	const thirdFloorStaff = ["3OBS CNA", "SA", "3A CNA", "3B CNA", "3C CNA", "", "3A Nurse", "3B Nurse", "3C Nurse"];
	cols = Array.from({ length: NURSE_COLS_AMOUNT }, (_, i) => String.fromCharCode("A".charCodeAt(0) + i + NURSE_COLS_AMOUNT + 1));
	const cnaRowCountThirdFloor = insertFloorValues(worksheet, "3RD", cols, startRow, thirdFloorStaff, thirdFloorShifts);
	return Math.max(cnaRowCountSecondFloor, cnaRowCountThirdFloor);
}

function addChargeNurseAndFloorHeaders(worksheet: Worksheet, shiftToNurseMap: any, orangeRowLine: number) {
	// Get day of week to determine which staff to show
	const day = worksheet.getCell("A3").value;
	const isWeekend = typeof day === "string" && (day.toLowerCase().includes("sun") || day.toLowerCase().includes("sat"));
	// Define special staff based on day of week
	const specialStaff = isWeekend ? ["Weekend Supervisor", "Weekend Charge", "Wound Care"] : ["Admissions Nurse", "Wound Care"];
	let currentRow = orangeRowLine;

	// Create orange header rows for each special staff
	const cols_2nd_floor = Array.from({ length: NURSE_COLS_AMOUNT }, (_, i) => String.fromCharCode("A".charCodeAt(0) + i));
	const cols_3rd_floor = Array.from({ length: NURSE_COLS_AMOUNT }, (_, i) => String.fromCharCode("A".charCodeAt(0) + i + NURSE_COLS_AMOUNT + 1));

	// Create orange header rows for each special staff member
	for (let i = 0; i < specialStaff.length; i++) {
		const currentHeaderRow = orangeRowLine + i;

		// Apply orange background to each cell individually instead of merging
		for (let j = 0; j < NURSE_COLS_AMOUNT; j++) {
			// Apply orange background to 2nd floor cells
			worksheet.getCell(`${cols_2nd_floor[j]}${currentHeaderRow}`).fill = {
				type: "pattern" as const,
				pattern: "solid" as const,
				fgColor: { argb: "FFFCD5B4" },
			};

			// Apply orange background to 3rd floor cells
			worksheet.getCell(`${cols_3rd_floor[j]}${currentHeaderRow}`).fill = {
				type: "pattern" as const,
				pattern: "solid" as const,
				fgColor: { argb: "FFFCD5B4" },
			};
		}
	}

	// Add each special staff row
	specialStaff.forEach((staffType) => {
		// Left side cell (2nd floor)
		worksheet.getCell(`${cols_2nd_floor[0]}${currentRow}`).value = `${staffType}`;
		worksheet.getCell(`${cols_2nd_floor[0]}${currentRow}`).border = {
			top: { style: "thin" },
			left: { style: "thin" },
			bottom: { style: "thin" },
			right: { style: "thin" },
		};
		worksheet.getCell(`${cols_2nd_floor[0]}${currentRow}`).alignment = {
			wrapText: true,
			horizontal: "center",
		};
		worksheet.getCell(`${cols_2nd_floor[0]}${currentRow}`).font = {
			size: 12,
		};

		// Check if we have data for these special staff
		if (shiftToNurseMap?.[staffType]?.[0]?.[staffType]) {
			const staffData = shiftToNurseMap[staffType][0][staffType][0];
			// First column already has staffType from above code
			// Second column should have nurse name
			// Third column should have role type
			worksheet.getCell(`${cols_2nd_floor[1]}${currentRow}`).value = staffData.nurse;
			worksheet.getCell(`${cols_2nd_floor[1]}${currentRow}`).alignment = {
				wrapText: true,
			};
			worksheet.getCell(`${cols_2nd_floor[1]}${currentRow}`).font = {
				size: 12,
			};

			worksheet.getCell(`${cols_2nd_floor[2]}${currentRow}`).value = staffData.role;
			worksheet.getCell(`${cols_2nd_floor[2]}${currentRow}`).alignment = {
				wrapText: true,
			};
			worksheet.getCell(`${cols_2nd_floor[2]}${currentRow}`).font = {
				size: 12,
			};

			// Fourth column should have other info if needed
			worksheet.getCell(`${cols_2nd_floor[3]}${currentRow}`).value = staffData.employmentType;
			worksheet.getCell(`${cols_2nd_floor[3]}${currentRow}`).alignment = {
				wrapText: true,
				horizontal: "center",
			};
			worksheet.getCell(`${cols_2nd_floor[3]}${currentRow}`).font = {
				size: 12,
			};
		}

		currentRow++;
	});
	// Add black divider row
	currentRow++;

	// 2nd Floor header
	worksheet.getCell(`${cols_2nd_floor[0]}${currentRow}`).value = "2nd Floor";
	worksheet.mergeCells(`${cols_2nd_floor[0]}${currentRow}:${cols_2nd_floor[NURSE_COLS_AMOUNT - 1]}${currentRow}`);
	worksheet.getCell(`${cols_2nd_floor[0]}${currentRow}`).alignment = {
		horizontal: "center",
	};
	worksheet.getCell(`${cols_2nd_floor[0]}${currentRow}`).font = {
		size: 18,
		bold: true,
	};

	// 3rd Floor header
	worksheet.getCell(`${cols_3rd_floor[0]}${currentRow}`).value = "3rd Floor";
	worksheet.mergeCells(`${cols_3rd_floor[0]}${currentRow}:${cols_3rd_floor[NURSE_COLS_AMOUNT - 1]}${currentRow}`);
	worksheet.getCell(`${cols_3rd_floor[0]}${currentRow}`).alignment = {
		horizontal: "center",
	};
	worksheet.getCell(`${cols_3rd_floor[0]}${currentRow}`).font = {
		size: 18,
		bold: true,
	};
}

function addNightShiftStaff(worksheet: Worksheet, shiftToNurseMap: any, startRow: number) {
	const secondFloorShifts = shiftToNurseMap["2ND FLOOR"]?.[1];

	const nightShiftSecond = ["2OBS CNA", "2A CNA", "2B CNA", "2C CNA", "", "2A Nurse", "2B Nurse", "2C Nurse"];
	let cols = Array.from({ length: NURSE_COLS_AMOUNT }, (_, i) => String.fromCharCode("A".charCodeAt(0) + i));
	const cnaRowCountSecondFloor = insertFloorValues(worksheet, "2ND", cols, startRow, nightShiftSecond, secondFloorShifts);

	const thirdFloorShifts = shiftToNurseMap["3RD FLOOR"]?.[1];
	const nightShiftThird = ["3OBS CNA", "3A CNA", "3B CNA", "3C CNA", "", "3A Nurse", "3B Nurse", "3C Nurse"];

	cols = Array.from({ length: NURSE_COLS_AMOUNT }, (_, i) => String.fromCharCode("A".charCodeAt(0) + i + NURSE_COLS_AMOUNT + 1));
	const cnaRowCountThirdFloor = insertFloorValues(worksheet, "3RD", cols, startRow, nightShiftThird, thirdFloorShifts);
	return Math.max(cnaRowCountSecondFloor, cnaRowCountThirdFloor);
}

function addBreakInformation(worksheet: Worksheet, breakRow: number) {
	worksheet.getCell(`A${breakRow + 1}`).value = "First break= 15minute";
	worksheet.getCell(`B${breakRow + 1}`).value = "Lunch Break = 30";
	worksheet.getCell(`C${breakRow + 1}`).value = "Third Break=15 minutes";
	worksheet.getCell(`A${breakRow + 2}`).value = "You must work 4 hours before first break occurs.";
	worksheet.getCell(`A${breakRow + 3}`).value = "No staff of the same disipline may take a lunch break together";
}

function addLegendAndStyling(worksheet: Worksheet, [nurseDayStart, nurseDayLength]: [number, number], [nurseNightStart, nurseNightLength]: [number, number]) {
	const first_char_3rd = String.fromCharCode("A".charCodeAt(0) + NURSE_COLS_AMOUNT + 1);

	const lightBlueFill = {
		type: "pattern" as const,
		pattern: "solid" as const,
		fgColor: { argb: "FFB8CCE4" },
	};

	// Apply light blue fill to nurse rows
	// Build arrays for day and night shift nurse cells
	const nurseCells = [
		...Array.from({ length: nurseDayLength }, (_, i) => `A${nurseDayStart + i}`),
		...Array.from({ length: nurseDayLength }, (_, i) => `${first_char_3rd}${nurseDayStart + i}`),
		...Array.from({ length: nurseNightLength }, (_, i) => `A${nurseNightStart + i}`),
		...Array.from({ length: nurseNightLength }, (_, i) => `${first_char_3rd}${nurseNightStart + i}`),
	];

	// Apply light blue fill to all nurse cells
	nurseCells.forEach((cell) => {
		worksheet.getCell(cell).fill = lightBlueFill;
	});
}

function addVerticalDividers(worksheet: Worksheet, cnaMaxRowCountDay: number, cnaMaxRowCountNight: number, maxDayLength: number, maxNightLength: number) {
	// Set column E to be very narrow and act as a divider
	worksheet.getColumn(String.fromCharCode("A".charCodeAt(0) + NURSE_COLS_AMOUNT)).width = 0.5; // Make it very narrow
	const startTableRow = 7;
	const rowsToSkip = [startTableRow + 1, startTableRow + 1 + cnaMaxRowCountDay + 1, startTableRow + 1 + maxDayLength + 5 + cnaMaxRowCountNight];
	const maxLine = startTableRow + maxDayLength + maxNightLength + 5;
	// Add black borders to columns D, E, and I for all rows
	for (let row = startTableRow; row <= maxLine; row++) {
		if (rowsToSkip.includes(row)) {
			continue;
		}
		const cell = worksheet.getCell(`E${row}`);
		cell.border = {
			left: { style: "thick", color: { argb: "000000" } },
			right: { style: "thick", color: { argb: "000000" } },
		};
	}
}

function convertShiftToHours(time: DateTime) {
	const hour = time.get("hour");
	const minutes = time.get("minute");
	return `${hour > 12 ? hour - 12 : hour}${minutes !== 0 ? `:${minutes.toString().padStart(2, "0")}` : ""}${hour >= 12 ? "pm" : "am"}`;
}

type shiftType = "Day" | "Night";
const shiftTransitionRules: Record<string, { transitionHour: number; startHour: number; targetShiftType: shiftType }> = {
	Day: { transitionHour: 19, startHour: 6, targetShiftType: "Night" },
	Night: { transitionHour: 7, startHour: 18, targetShiftType: "Day" },
} as const;

// Helper to get shift transition and split boundary times for a shift
const getShiftStartTime = (nurse: NurseInShift) => {
	const { transitionHour } = shiftTransitionRules[nurse.shiftType];
	const splitHour = transitionHour - 1 > 0 ? transitionHour - 1 : 23;

	return {
		nextShiftStart: nurse.startTime.set({ hour: transitionHour, minute: 0, second: 0 }),
		splitHours: nurse.startTime.set({ hour: splitHour, minute: 0, second: 0 }),
	};
};

// Returns a new shift object if split is needed, never mutates input
const getSplittedShift = (nurse: NurseInShift) => {
	if (nurse.shiftType !== "Day") return null;

	const { nextShiftStart, splitHours } = getShiftStartTime(nurse);
	if (nurse.endTime >= nextShiftStart)
		return {
			...nurse,
			startTime: splitHours,
			endTime: nurse.endTime,
			shiftType: shiftTransitionRules[nurse.shiftType].targetShiftType,
		};

	return null;
};

const getShiftEndTime = (nurse: NurseInShift) => {
	if (nurse.shiftType !== "Day") return nurse.endTime;

	const { nextShiftStart, splitHours } = getShiftStartTime(nurse);

	return nurse.endTime >= nextShiftStart ? splitHours : nurse.endTime;
};

function arrangeNursesData(hospitalData: HospitalData, weekIndex: number, dayIndex: number, department_id: string, lastNameFirst: boolean) {
	// Create dictionary mapping shift details to nurse names
	const shiftToNurseMap: any = {};
	const shiftTypeToIndex = new Map(hospitalData.weeks[weekIndex].weeklyShifts.map((shift, index) => [shift.type, index]));

	const allShiftsNurses = hospitalData.weeks[weekIndex].weeklyShifts.reduce((result, shift) => {
		const nurses = shift.days[dayIndex]?.nurses ?? [];

		nurses.forEach((nurse: NurseInShift) => {
			if (nurse.startTime.get("hour") >= shiftTransitionRules.Night.startHour || nurse.startTime.get("hour") < shiftTransitionRules.Day.startHour) {
				result.push({ ...nurse, shiftType: "Night" });
			} else {
				const dayNurse = { ...nurse, shiftType: "Day" };

				result.push({ ...dayNurse, endTime: getShiftEndTime(dayNurse) });
				const extraShift = getSplittedShift(dayNurse);

				if (extraShift) {
					result.push({ ...extraShift, shiftType: "Night" });
				}
			}
		});

		return result;
	}, [] as NurseInShift[]);

	allShiftsNurses.forEach((nurse: NurseInShift) => {
		const fullNurse: Nurse | undefined = hospitalData.nurses.find((n: Nurse) => n.id === nurse.nurseId);
		if (fullNurse === undefined || nurse.departmentId !== department_id) return;

		const role = inverseRolesMap[fullNurse.role];

		const employmentType = fullNurse.employmentType
			.split(/(?=[A-Z])/)
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(" ");

		const toTitleCase = (str: string) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
		const nurseType = role === NurseRole.CNA ? "CNA" : toTitleCase("NURSE");
		let floor: string;
		let room: string;
		if (
			nurse.location?.toLocaleLowerCase() === "wound care" ||
			nurse.location?.toLocaleLowerCase() === "weekend supervisor" ||
			nurse.location?.toLocaleLowerCase() === "admissions nurse" ||
			nurse.location?.toLocaleLowerCase() === "weekend charge"
		) {
			floor = nurse.location;
			room = "";
		} else {
			[floor, room] = nurse.location?.split("-").map((s) => s?.trim()) ?? [];
		}

		// shower aid rooms are in format roomSA
		const isSARoom = room === "SA" || /^\d+SA$/.test(room);
		let shiftKey: string;
		if (floor && !room) {
			shiftKey = floor;
		} else {
			shiftKey = isSARoom ? "SA" : `${room} ${nurseType}`;
		}

		const shiftIndex = shiftTypeToIndex.get(nurse.shiftType) as number;
		const nurseName = formatPersonFullName(fullNurse, lastNameFirst);

		if (!shiftToNurseMap[floor]) {
			shiftToNurseMap[floor] = {};
		}
		if (!shiftToNurseMap[floor][shiftIndex]) {
			shiftToNurseMap[floor][shiftIndex] = {
				census: getShiftCensus(hospitalData.weeks[weekIndex], shiftIndex, dayIndex),
			};
		}
		if (!shiftToNurseMap[floor][shiftIndex][shiftKey]) {
			shiftToNurseMap[floor][shiftIndex][shiftKey] = [];
		}
		const hours = nurse.endTime.diff(nurse.startTime, "hours").as("hours");
		const notes = buildNurseNotes(nurse, hospitalData, { includeAgency: false, includeCharge: false });
		shiftToNurseMap[floor][shiftIndex][shiftKey].push({
			employmentType: employmentType,
			role: role,
			type: nurse.type,
			notes: notes,
			nurse: nurseName,
			hours: hours,
			shift: `${convertShiftToHours(nurse.startTime)}-${convertShiftToHours(nurse.endTime)}`,
			startTime: nurse.startTime,
			// `calculateTotalNursingHours` only ever sees this map, so the union has to be carried on it.
			union: fullNurse.union ?? "",
		});
	});

	// Sort nurses within each shift by start time (reverse order for night shift)
	Object.keys(shiftToNurseMap).forEach((floor) => {
		Object.keys(shiftToNurseMap[floor]).forEach((shiftIndex) => {
			Object.keys(shiftToNurseMap[floor][shiftIndex]).forEach((shiftKey) => {
				if (Array.isArray(shiftToNurseMap[floor][shiftIndex][shiftKey])) {
					shiftToNurseMap[floor][shiftIndex][shiftKey].sort((a: any, b: any) => {
						// Night shift (index 1) should be sorted in reverse
						const sortOrder = shiftIndex === "1" ? -1 : 1;
						return sortOrder * (new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
					});
				}
			});
		});
	});

	return shiftToNurseMap;
}

function getDayAndNightShiftLengths(shiftToNurseMap: any, floor: string) {
	const lengths = [0, 0]; // [dayLength, nightLength]
	[0, 1].forEach((shiftIndex) => {
		if (shiftToNurseMap[floor] && shiftToNurseMap[floor][shiftIndex]) {
			Object.keys(shiftToNurseMap[floor][shiftIndex]).forEach((key) => {
				if (key !== "census" && !key.includes("FLOOR") && Array.isArray(shiftToNurseMap[floor][shiftIndex][key])) {
					lengths[shiftIndex] += shiftToNurseMap[floor][shiftIndex][key].length;
				}
			});
		}
	});
	return lengths;
}

function paddingFloorShifts(shiftToNurseMap: any) {
	// Normalize array lengths between floors
	Object.keys(shiftToNurseMap).forEach((floor1) => {
		Object.keys(shiftToNurseMap).forEach((floor2) => {
			if (floor1 !== floor2) {
				for (const shift of [0, 1]) {
					if (shiftToNurseMap[floor1][shift] && shiftToNurseMap[floor2][shift]) {
						// Check shifts
						const floorStaff = shift === 0 ? dayFloorStaff : nightFloorStaff;
						// Remove empty strings from floorStaff array
						const filteredFloorStaff: string[] = floorStaff.filter((staff) => staff !== "");
						filteredFloorStaff.forEach((role) => {
							// Extract floor numbers for comparison
							const floor1Num = Number.parseInt(floor1.split(" ")[0].replace(/[^\d]/g, ""));
							const floor2Num = Number.parseInt(floor2.split(" ")[0].replace(/[^\d]/g, ""));
							let role1Key: string;
							let role2Key: string;
							if (role === "SA") {
								role1Key = role;
								role2Key = role;
							} else {
								role1Key = `${floor1Num}${role}`;
								role2Key = `${floor2Num}${role}`;
							}

							const length1 = shiftToNurseMap[floor1][shift]?.[role1Key]?.length || 0;
							const length2 = shiftToNurseMap[floor2][shift]?.[role2Key]?.length || 0;
							if (length1 === length2 && length1 !== 0) return;
							// Handle case where either array is longer
							const maxLength = Math.max(length1, length2, 1);
							for (const [floor, length, roleKey] of [
								[floor1, length1, role1Key],
								[floor2, length2, role2Key],
							]) {
								if (!shiftToNurseMap[floor][shift]?.[roleKey]) {
									shiftToNurseMap[floor][shift][roleKey] = [];
								}
								// Pad floor array if needed
								for (let i = length; i < maxLength; i++) {
									shiftToNurseMap[floor][shift][roleKey].push({
										nurse: "",
										notes: "",
										employmentType: "",
										shift: "",
									});
								}
							}
						});
					}
				}
			}
		});
	});
}

export async function createDayViewLowry(
	worksheet: Worksheet,
	hospitalData: any,
	weekIndex: any,
	dayIndex: any,
	department: any,
	day: any,
	date: any,
	summary: boolean,
	lastNameFirst: boolean,
	jobManagementEnabled = false,
) {
	const shiftToNurseMap = arrangeNursesData(hospitalData, weekIndex, dayIndex, department.department_id, lastNameFirst);
	paddingFloorShifts(shiftToNurseMap);
	const firstFloor = Object.keys(shiftToNurseMap)[0];
	const [dayLength, nightLength] = getDayAndNightShiftLengths(shiftToNurseMap, firstFloor);

	// Basic setup
	setColumnWidths(worksheet);
	addTitle(worksheet, "A1", "E1", "DAY SCHEDULE");

	// Top section
	addDayAndDateFields(worksheet, day, date);
	addCensusAndStaffingData(worksheet, shiftToNurseMap, department.department_id, department.breakRulesConfig, jobManagementEnabled); // show Total Census, Total Nursing Hours, PPD

	if (summary) {
		// Add the staffing summary table at row 6
		return addStaffingSummaryTableFloors(worksheet, 6, shiftToNurseMap);
	}

	// Day shift section
	addFloorHeaders(worksheet); // "2nd Floor" | "3rd Floor"
	const startRow = 9;
	const cnaMaxRowCountDay = addDayShiftStaff(worksheet, shiftToNurseMap, startRow);

	// Charge nurse section
	addChargeNurseAndFloorHeaders(worksheet, shiftToNurseMap, startRow + dayLength + 1);

	const dayToCheck = worksheet.getCell("A3").value;
	const isWeekend = typeof dayToCheck === "string" && (dayToCheck.toLowerCase().includes("sun") || dayToCheck.toLowerCase().includes("sat"));

	// Night shift section
	// 5: 1 empty row between cna and nurses for day shift, 1 orange row, 1 charge nurse row, 1 black row, 1 floor row
	const cnaMaxRowCountNight = addNightShiftStaff(worksheet, shiftToNurseMap, startRow + dayLength + (isWeekend ? 8 : 7));

	// Break information
	// adding 1 for empty row between cna and nurses for night shift
	addBreakInformation(worksheet, startRow + dayLength + (isWeekend ? 9 : 8) + nightLength);

	// Add vertical dividers between floors
	addVerticalDividers(worksheet, cnaMaxRowCountDay, cnaMaxRowCountNight, dayLength + 1, nightLength);

	// Add horizontal dividers between shifts
	// 3: 1 empty row between cna and nurses for day shift, 1 orange row, 1 charge nurse row
	addHorizontalDividers(worksheet, startRow + dayLength + (isWeekend ? 6 : 5), NURSE_COLS_AMOUNT * 2 + 1);

	// Add borders to all cells
	addBorders(worksheet);

	addLegendAndStyling(
		worksheet,
		[startRow + cnaMaxRowCountDay + 1, dayLength - cnaMaxRowCountDay],
		[startRow + dayLength + (isWeekend ? 9 : 8) + cnaMaxRowCountNight, nightLength - cnaMaxRowCountNight],
	);
}
